// MARK: - Core Audio Profile Types for AI Prompting

/** Music mood categories for AI-driven selection */
export type MusicMood =
  | 'sophisticated'
  | 'playful'
  | 'dramatic'
  | 'energetic'
  | 'jazz'
  | 'romantic';

export const MUSIC_MOODS: MusicMood[] = [
  'sophisticated',
  'playful',
  'dramatic',
  'energetic',
  'jazz',
  'romantic',
];

/** Sound effect categories for contextual placement */
export type SoundEffectCategory =
  | 'reactions'
  | 'objects'
  | 'comedy'
  | 'transitions';

export const SFX_CATEGORIES: SoundEffectCategory[] = [
  'reactions',
  'objects',
  'comedy',
  'transitions',
];

/** Sound effect timing relative to trigger phrase */
export type SoundEffectTiming = 'before' | 'during' | 'after';

export const SFX_TIMINGS: SoundEffectTiming[] = ['before', 'during', 'after'];

/** Contextual tags for photo and roast analysis */
export type ContextualTag =
  | 'formal'
  | 'casual'
  | 'party'
  | 'professional'
  | 'vintage'
  | 'modern'
  | 'outdoor'
  | 'indoor';

export const CONTEXTUAL_TAGS: ContextualTag[] = [
  'formal',
  'casual',
  'party',
  'professional',
  'vintage',
  'modern',
  'outdoor',
  'indoor',
];

// Music tracks are cataloged in ./musicLibrary (the single source of truth).
// MusicTrack + MUSIC_TRACKS are DERIVED from MUSIC_LIBRARY's keys and re-exported
// here so existing `import … from './types'` paths keep working. (Type-only import
// + a single value re-export — musicLibrary imports from types type-only, so there
// is no runtime import cycle.)
import type { MusicTrack } from './musicLibrary';
export type { MusicTrack };
export { MUSIC_TRACKS } from './musicLibrary';

// MARK: - Audio Profile Models

/** Sound effect opportunity identified by AI */
export interface SoundEffectOpportunity {
  triggerPhrase: string; // exact phrase from roast script
  suggestedCategory: SoundEffectCategory; // enum-constrained
  timing: SoundEffectTiming; // enum-constrained
  confidence: number; // 0.0-1.0
}

/** Sound effect cue for playback */
export interface SoundEffectCue {
  effectName: string;
  triggerTime: number;
  volume: number; // 0.0-1.0
  category: SoundEffectCategory;
}

/** Complete audio profile generated by AI analysis */
export interface AudioProfile {
  musicMood: MusicMood; // enum-constrained
  energyLevel: number; // 0.0-1.0
  soundEffectOpportunities: SoundEffectOpportunity[];
  contextualTags: ContextualTag[]; // enum-constrained array
}

/** Selected audio assets for a roast */
export interface AudioAssets {
  musicTrack: MusicTrack;
  cues: SoundEffectCue[];
  profile: AudioProfile;
}

// MARK: - Helper Functions

/** Clamp a number to the range [0, 1] */
export function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

// MARK: - Default Values

/** Default profile for fallback scenarios (mirrors AudioProfile.default from iOS) */
export const DEFAULT_AUDIO_PROFILE: AudioProfile = {
  musicMood: 'jazz',
  energyLevel: 0.5,
  soundEffectOpportunities: [],
  contextualTags: ['casual'],
};
